import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faPlus, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Typeahead, Highlighter } from 'react-bootstrap-typeahead';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import './TaskTracker.css'; // 引入CSS文件
import { sendMarkdownToWeChatBot } from '../services/weChatBotServiceES6'; // 导入微信机器人服务


const TaskTracker = () => {
    const [tasks, setTasks] = useState([]);
    const [currentTask, setCurrentTask] = useState(localStorage.getItem('currentTask') || '');
    const [taskCategory, setTaskCategory] = useState(localStorage.getItem('taskCategory') || '');
    const [taskCategories, setTaskCategories] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [isTiming, setIsTiming] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [newTask, setNewTask] = useState({
        task: '',
        category: '',
        startTime: '',
        endTime: '',
        weight: 1,
        expectedTime: 0,
        investedTime: 0,
        isComplete: false,
        goal: null,
        notes: '' //备注
    });
    const [startDate, setStartDate] = useState(moment().subtract(7, 'days').toDate());
    const [endDate, setEndDate] = useState(new Date());
    const [editingCategory, setEditingCategory] = useState(null);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

    const fetchTasks = async (startDate, endDate) => {
        const adjustedEndDate = moment(endDate).endOf('day').toISOString();
        const response = await axios.get('/tasks', {
            params: {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: adjustedEndDate
            }
        });
        setTasks(response.data);
    };

    const fetchCategories = async () => {
        const response = await axios.get('/categories');
        setTaskCategories(response.data);
    };

    useEffect(() => {
        fetchTasks(startDate, endDate);
        fetchCategories();
    }, [startDate, endDate]);

    useEffect(() => {
        localStorage.setItem('taskCategories', JSON.stringify(taskCategories));
    }, [taskCategories]);

    useEffect(() => {
        let timer;
        if (isTiming) {
            timer = setInterval(() => {
                setElapsedTime(moment().diff(moment(startTime), 'seconds'));
            }, 1000);
        } else {
            setElapsedTime(0);
        }
        return () => clearInterval(timer);
    }, [isTiming, startTime]);

    useEffect(() => {
        const savedTimingState = localStorage.getItem('isTiming');
        const savedStartTime = localStorage.getItem('startTime');

        if (savedTimingState === 'true' && savedStartTime) {
            setIsTiming(true);
            setStartTime(moment(savedStartTime));
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('isTiming', isTiming);
        if (isTiming && startTime) {
            localStorage.setItem('startTime', startTime.toISOString());
        } else {
            localStorage.removeItem('startTime');
        }
    }, [isTiming, startTime]);

    useEffect(() => {
        localStorage.setItem('currentTask', currentTask);
    }, [currentTask]);

    useEffect(() => {
        localStorage.setItem('taskCategory', taskCategory);
    }, [taskCategory]);

    // 监听计时状态和时间变化，更新网页标题
    useEffect(() => {
        if (isTiming) {
            const truncatedTask = currentTask.length > 9 ? `${currentTask.substring(0, 9)}...` : currentTask;
            document.title = `${truncatedTask} - ${moment.utc(elapsedTime * 1000).format('HH:mm:ss')}`;
        } else {
            document.title = '云崽';
        }
    }, [isTiming, elapsedTime, currentTask]);


    const handleStart = async () => {
        const now = moment();
        setStartTime(now);
        setIsTiming(true);
        localStorage.setItem('isTiming', true);
        localStorage.setItem('startTime', now.toISOString());

        // 发送开始计时消息
        // 获取当前时间作为开始时间，并格式化为字符串
        const formattedStartTime = now.format('HH:mm');
        const message = `任务 "${currentTask}" 开始计时，开始时间：${formattedStartTime}`;
        await sendMarkdownToWeChatBot(message);
    };

    const handleEnd = async () => {
        const endTime = moment();
        const taskDuration = endTime.diff(startTime, 'minutes'); // duration in minutes
        const newTask = { task: currentTask, category: taskCategory, duration: taskDuration, startTime, endTime, weight: taskCategories.find(cat => cat.name === taskCategory).weight };
        const response = await axios.post('/tasks', newTask);
        setTasks([...tasks, response.data]);
        setCurrentTask('');
        setTaskCategory('');
        setStartTime(null);
        setIsTiming(false);
        localStorage.removeItem('isTiming');
        localStorage.removeItem('startTime');
        localStorage.removeItem('currentTask');
        localStorage.removeItem('taskCategory');

        // 获取当前任务的开始和结束时间，并格式化为字符串
        const formattedStartTime = startTime.format('HH:mm');
        const formattedEndTime = endTime.format('HH:mm');
        // 发送结束计时消息
        const message = `任务 "${currentTask}" 结束计时，开始时间：${formattedStartTime}，结束时间：${formattedEndTime}，任务累计 ${taskDuration} 分钟。`;
        await sendMarkdownToWeChatBot(message);
    };

    const addTaskCategory = async () => {
        const newCategory = prompt('Enter new task category:');
        const newWeight = prompt('Enter weight for the new category:', 1);
        if (newCategory && !taskCategories.find(cat => cat.name === newCategory)) {
            const response = await axios.post('/categories', { name: newCategory, weight: newWeight });
            setTaskCategories([...taskCategories, response.data]);
        }
    };

    const handleDeleteCategory = async (categoryName) => {
        if (window.confirm(`Are you sure you want to delete the category: ${categoryName}?`)) {
            try {
                await axios.delete(`/categories/${categoryName}`);
                setTaskCategories(taskCategories.filter(cat => cat.name !== categoryName));
            } catch (error) {
                console.error('Error deleting category:', error);
            }
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setShowEditCategoryModal(true);
    };

    const handleUpdateCategory = async () => {
        const { name, weight } = editingCategory;
        try {
            const response = await axios.put(`/categories/${editingCategory._id}`, { name, weight });
            setTaskCategories(taskCategories.map(cat => (cat._id === editingCategory._id ? response.data : cat)));
            setShowEditCategoryModal(false);
            setEditingCategory(null);
        } catch (error) {
            console.error('Error updating category:', error);
        }
    };

    const handleEditTask = (task, field, value) => {
        const updatedTask = { ...task, [field]: value };
        setTasks(tasks.map(t => (t._id === task._id ? updatedTask : t)));
        saveTask(updatedTask);
    };

    const saveTask = async (task) => {
        const updatedTask = {
            ...task,
            startTime: moment(task.startTime).toISOString(),
            endTime: moment(task.endTime).toISOString(),
        };
        await axios.put(`/tasks/${task._id}`, updatedTask);
    };

    const handleDeleteTask = async (id) => {
        await axios.delete(`/tasks/${id}`);
        setTasks(tasks.filter(task => task._id !== id));
    };

    const handleAddTask = async () => {
        const { task, category, startTime, endTime, weight, expectedTime, investedTime, isComplete, goal, notes } = newTask;
        const today = moment().format('YYYY-MM-DD');
        const startDateTime = moment(`${today} ${startTime}`, 'YYYY-MM-DD HH:mm').toISOString();
        const endDateTime = moment(`${today} ${endTime}`, 'YYYY-MM-DD HH:mm').toISOString();
        const taskDuration = moment(endDateTime).diff(moment(startDateTime), 'minutes');
        const response = await axios.post('/tasks', { task, category, duration: taskDuration, startTime: startDateTime, endTime: endDateTime, weight, expectedTime, investedTime, isComplete, goal, notes });
        setTasks([...tasks, response.data]);
        setShowAddTaskModal(false);
        setNewTask({
            task: '',
            category: '',
            startTime: '',
            endTime: '',
            weight: 1,
            expectedTime: 0,
            investedTime: 0,
            isComplete: false,
            goal: null,
            notes: ''
        });
    };

    const groupTasksByDate = (tasks) => {
        return tasks.reduce((groups, task) => {
            const date = moment(task.startTime).format('YYYY-MM-DD');
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(task);
            return groups;
        }, {});
    };

    const calculateDailyScore = (tasks) => {
        return tasks.reduce((total, task) => total + (task.duration * task.weight), 0);
    };

    const tasksGroupedByDate = groupTasksByDate(tasks);

    return (
        <div className="container">
            <h1 className="my-4">Task Tracker</h1>
            <div className="card mb-4">
                <div className="card-body">
                    <div className="mb-3">
                        <div className="d-flex">
                            <input
                                type="text"
                                className="form-control me-2"
                                value={currentTask}
                                onChange={(e) => setCurrentTask(e.target.value)}
                                placeholder="Enter task"
                            />
                            <button className="btn btn-warning" onClick={() => setShowAddTaskModal(true)}>
                                <FontAwesomeIcon icon={faEdit} /> Add Task
                            </button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="d-flex">
                            <Typeahead
                                id="task-category"
                                options={taskCategories.map(cat => cat.name)}
                                selected={taskCategory ? [taskCategory] : []}
                                onChange={(selected) => {
                                    setTaskCategory(selected[0] || '');
                                    const selectedCategory = taskCategories.find(cat => cat.name === selected[0]);
                                    if (selectedCategory) {
                                        setNewTask({ ...newTask, weight: selectedCategory.weight });
                                    }
                                }}
                                placeholder="Select category"
                                allowNew
                                newSelectionPrefix="Add a new category: "
                                renderMenuItemChildren={(option, { text }) => (
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                        <Highlighter search={text}>
                                            {option}
                                        </Highlighter>
                                        <div>
                                            <button
                                                className="btn btn-sm btn-danger me-2"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDeleteCategory(option);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const category = taskCategories.find(cat => cat.name === option);
                                                    handleEditCategory(category);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                className="flex-grow-1 me-2"
                            />
                            <button className="btn btn-success" onClick={addTaskCategory}>
                                <FontAwesomeIcon icon={faPlus} /> Add Category
                            </button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <button className="btn btn-primary me-2" onClick={isTiming ? handleEnd : handleStart}>
                            <FontAwesomeIcon icon={isTiming ? faStop : faPlay} /> {isTiming ? 'Stop' : 'Start'}
                        </button>
                        {isTiming && <span className="timer">{moment.utc(elapsedTime * 1000).format('HH:mm:ss')}</span>}
                    </div>
                    <div className="mb-3">
                        <label>Select Date Range:</label>
                        <div className="d-flex">
                            <DatePicker
                                selected={startDate}
                                onChange={date => setStartDate(date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                className="form-control me-2"
                            />
                            <DatePicker
                                selected={endDate}
                                onChange={date => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                className="form-control"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div>
                {Object.keys(tasksGroupedByDate).sort((a, b) => moment(b).diff(moment(a))).map(date => (
                    <div key={date}>
                        <h2>{date} - Score: {calculateDailyScore(tasksGroupedByDate[date])}</h2>
                        <table className="table table-striped">
                            <thead>
                            <tr>
                                <th className="task-column">Task</th>
                                <th className="category-column">Category</th>
                                <th className="time-column">Start Time</th>
                                <th className="time-column">End Time</th>
                                <th className="duration-column">Duration (mins)</th>
                                <th className="weight-column">Weight</th>
                                <th className="score-column">Score</th>
                                <th className="notes-column">Notes</th>
                                <th className="actions-column">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {tasksGroupedByDate[date].map((task, index) => (
                                <tr key={index}>
                                    <td
                                        contentEditable
                                        suppressContentEditableWarning
                                        onBlur={(e) => handleEditTask(task, 'task', e.target.innerText)}
                                        className="task-column"
                                    >
                                        {task.task}
                                    </td>
                                    <td className="category-column">
                                        <Typeahead
                                            id={`task-category-${task._id}`}
                                            options={taskCategories.map(cat => cat.name)}
                                            selected={task.category ? [task.category] : []}
                                            onChange={(selected) => handleEditTask(task, 'category', selected[0] || '')}
                                            placeholder="Select category"
                                            allowNew
                                            newSelectionPrefix="Add a new category: "
                                        />
                                    </td>
                                    <td className="time-column">
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={moment(task.startTime).format('HH:mm')}
                                            onChange={(e) => handleEditTask(task, 'startTime', moment(task.startTime).set({
                                                hour: moment(e.target.value, 'HH:mm').hours(),
                                                minute: moment(e.target.value, 'HH:mm').minutes()
                                            }))}
                                        />
                                    </td>
                                    <td className="time-column">
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={moment(task.endTime).format('HH:mm')}
                                            onChange={(e) => handleEditTask(task, 'endTime', moment(task.endTime).set({
                                                hour: moment(e.target.value, 'HH:mm').hours(),
                                                minute: moment(e.target.value, 'HH:mm').minutes()
                                            }))}
                                        />
                                    </td>
                                    <td className="duration-column">{task.duration}</td>
                                    <td className="weight-column">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={task.weight}
                                            onChange={(e) => handleEditTask(task, 'weight', parseFloat(e.target.value))}
                                        />
                                    </td>
                                    <td className="score-column">{task.duration * task.weight}</td>
                                    <td
                                        contentEditable
                                        suppressContentEditableWarning
                                        onBlur={(e) => handleEditTask(task, 'notes', e.target.innerText)}
                                        className="notes-column"
                                    >
                                        {task.notes}
                                    </td>
                                    <td className="actions-column">
                                        <button className="btn btn-sm btn-danger ms-2" onClick={() => handleDeleteTask(task._id)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            {/* 补录任务的模态框 */}
            {showAddTaskModal && (
                <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Task</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddTaskModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newTask.task}
                                        onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                                        placeholder="Enter task"
                                    />
                                </div>
                                <div className="mb-3">
                                    <Typeahead
                                        id="new-task-category"
                                        options={taskCategories.map(cat => cat.name)}
                                        selected={newTask.category ? [newTask.category] : []}
                                        onChange={(selected) => setNewTask({ ...newTask, category: selected[0] || '' })}
                                        placeholder="Select category"
                                        allowNew
                                        newSelectionPrefix="Add a new category: "
                                    />
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newTask.startTime}
                                        onChange={(e) => setNewTask({ ...newTask, startTime: e.target.value })}
                                        placeholder="Select start time"
                                    />
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newTask.endTime}
                                        onChange={(e) => setNewTask({ ...newTask, endTime: e.target.value })}
                                        placeholder="Select end time"
                                    />
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={newTask.weight}
                                        onChange={(e) => setNewTask({ ...newTask, weight: parseFloat(e.target.value) })}
                                        placeholder="Enter weight"
                                    />
                                </div>
                                <div className="mb-3">
                                    <textarea
                                        className="form-control"
                                        value={newTask.notes}
                                        onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })}
                                        placeholder="Enter notes"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddTaskModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleAddTask}>Add Task</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 编辑分类的模态框 */}
            {showEditCategoryModal && editingCategory && (
                <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Category</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditCategoryModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={editingCategory.name}
                                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                                        placeholder="Enter category name"
                                    />
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={editingCategory.weight}
                                        onChange={(e) => setEditingCategory({ ...editingCategory, weight: parseFloat(e.target.value) })}
                                        placeholder="Enter weight"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditCategoryModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleUpdateCategory}>Save Changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskTracker;
